// ============================================================================
// Module Analysis: Flexible & Customizable Dashboard API Client Service
// ============================================================================

import { apiRequest } from '../../shared/api';
import {
  CustomAnalysisDashboard,
  CustomAnalysisWidget,
  CreateDashboardDTO,
  UpdateDashboardDTO,
  CreateWidgetDTO,
  UpdateWidgetDTO,
  AnalysisQueryFilter,
  AnalysisQueryResult,
  DataSourceCatalogItem,
  DrilldownRecordItem,
  AnalysisDataSource,
} from './types';

function unwrapResponse<T>(res: any, fallback: T): T {
  if (res === null || res === undefined) return fallback;
  // If outer envelope exists (has 'success' and 'data')
  if (res && typeof res === 'object' && 'success' in res && res.data !== undefined) {
    return res.data as T;
  }
  return res as T;
}

export const analysisService = {
  // 1. Catalog & Metadata
  async getCatalog(): Promise<DataSourceCatalogItem[]> {
    const res = await apiRequest<any>('/analysis/catalog');
    const data = unwrapResponse<DataSourceCatalogItem[]>(res, []);
    return Array.isArray(data) ? data : [];
  },

  // 2. Dashboards CRUD
  async getDashboards(): Promise<CustomAnalysisDashboard[]> {
    const res = await apiRequest<any>('/analysis/dashboards');
    const data = unwrapResponse<CustomAnalysisDashboard[]>(res, []);
    return Array.isArray(data) ? data : [];
  },

  async getDashboardById(id: string): Promise<CustomAnalysisDashboard> {
    const res = await apiRequest<any>(`/analysis/dashboards/${id}`);
    return unwrapResponse<CustomAnalysisDashboard>(res, null as any);
  },

  async createDashboard(dto: CreateDashboardDTO): Promise<CustomAnalysisDashboard> {
    const res = await apiRequest<any>('/analysis/dashboards', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
    return unwrapResponse<CustomAnalysisDashboard>(res, null as any);
  },

  async updateDashboard(id: string, dto: UpdateDashboardDTO): Promise<CustomAnalysisDashboard> {
    const res = await apiRequest<any>(`/analysis/dashboards/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dto),
    });
    return unwrapResponse<CustomAnalysisDashboard>(res, null as any);
  },

  async deleteDashboard(id: string): Promise<boolean> {
    const res = await apiRequest<any>(`/analysis/dashboards/${id}`, {
      method: 'DELETE',
    });
    return res?.success !== false;
  },

  // 3. Widgets CRUD & Layout Reordering
  async createWidget(dashboardId: string, dto: CreateWidgetDTO): Promise<CustomAnalysisWidget> {
    const res = await apiRequest<any>(`/analysis/dashboards/${dashboardId}/widgets`, {
      method: 'POST',
      body: JSON.stringify(dto),
    });
    return unwrapResponse<CustomAnalysisWidget>(res, null as any);
  },

  async updateWidget(widgetId: string, dto: UpdateWidgetDTO): Promise<CustomAnalysisWidget> {
    const res = await apiRequest<any>(`/analysis/widgets/${widgetId}`, {
      method: 'PUT',
      body: JSON.stringify(dto),
    });
    return unwrapResponse<CustomAnalysisWidget>(res, null as any);
  },

  async deleteWidget(widgetId: string): Promise<boolean> {
    const res = await apiRequest<any>(`/analysis/widgets/${widgetId}`, {
      method: 'DELETE',
    });
    return res?.success !== false;
  },

  async reorderWidgets(dashboardId: string, widgetIds: string[]): Promise<boolean> {
    const res = await apiRequest<any>(`/analysis/dashboards/${dashboardId}/reorder`, {
      method: 'POST',
      body: JSON.stringify({ widgetIds }),
    });
    return res?.success !== false;
  },

  // 4. Live Data Query & Interactive Cross-Filtering
  async queryWidgetData(widgetId: string, filters?: AnalysisQueryFilter): Promise<AnalysisQueryResult> {
    const res = await apiRequest<any>(`/analysis/widgets/${widgetId}/query`, {
      method: 'POST',
      body: JSON.stringify({ filters }),
    });
    return unwrapResponse<AnalysisQueryResult>(res, { data: [], series: [], total: 0 } as any);
  },

  // 5. Visual Live Preview (For Modal Drafts)
  async previewQuery(widget: CreateWidgetDTO, filters?: AnalysisQueryFilter): Promise<AnalysisQueryResult> {
    const res = await apiRequest<any>('/analysis/preview-query', {
      method: 'POST',
      body: JSON.stringify({ widget, filters }),
    });
    return unwrapResponse<AnalysisQueryResult>(res, { data: [], series: [], total: 0 } as any);
  },

  // 6. Drill-Down Records Inspection (Opens the actual Reports/Occurrences)
  async getDrilldown(params: {
    dataSource: AnalysisDataSource;
    dimension?: string;
    dimensionValue?: string;
    filters?: AnalysisQueryFilter;
  }): Promise<DrilldownRecordItem[]> {
    const res = await apiRequest<any>('/analysis/drilldown', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    const data = unwrapResponse<DrilldownRecordItem[]>(res, []);
    return Array.isArray(data) ? data : [];
  },
};
